import {
  WhmAutocomplete,
  WhmBox,
  WhmButton,
  WhmFade,
  WhmStack,
  WhmTextField,
  WhmTypography,
} from "@securitize/reactjs-whm";
import intlTelInput from "intl-tel-input/intlTelInputWithUtils";
import secLogoPositive from "@securitize/assets-sec-ui/dist/logos/svg/sec-positive.svg";
import { useContext, useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { statesUS } from "./countriesList";
import TranslationKeys from "../../contexts/I18nContext/translationKeys";
import { Translate } from "../Translation/Translate";
import { FormType } from "./types";
import { getDialCodeByCountryCode } from "../../helpers/phoneHelper";
import CountryTextField from "../shared/CountryTextField/CountryTextField";
import { PulsatingSpinner } from "../shared/PulsatingSpinner";
import { ErrorPage } from "../shared/ErrorPage";
import { useCountries } from "../shared/CountryTextField/CountriesContext";
import { MFEContext } from "../../contexts/MFEContext";
import { MIXPANEL_EVENTS } from "../../helpers/mixpanel";

export default function PhoneStep({
  filler,
  resizeOnChange,
}: {
  filler?: React.ReactNode;
  resizeOnChange?: () => void;
}) {
  const formik = useFormikContext<FormType>();
  const countriesQuery = useCountries();
  const { mixPanel } = useContext(MFEContext);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (formik.values.country && formik.values.phone && !trackedRef.current) {
      trackedRef.current = true;
      mixPanel.track(MIXPANEL_EVENTS.REGISTRATION.COUNTRY_PHONE);
    }
  }, [formik.values.country, formik.values.phone, mixPanel]);

  if (countriesQuery.error) {
    return (
      <WhmStack sx={{ minHeight: "100%" }} spacing={6} alignItems="center">
        <ErrorPage
          errorCode={countriesQuery.error.correlationId}
          onRefresh={countriesQuery.refetch}
        />
      </WhmStack>
    );
  }

  if (countriesQuery.isLoading) {
    return (
      <WhmBox sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <PulsatingSpinner />
      </WhmBox>
    );
  }

  return (
    <WhmStack sx={{ minHeight: "100%" }} spacing={6} alignItems="center">
      <WhmBox
        component="img"
        sx={{ height: "24px" }}
        src={secLogoPositive}
        alt="Securitize Logo"
      />
      <WhmStack alignItems="center" sx={{ marginBottom: "8px !important" }}>
        <WhmTypography variant="h4" align="center">
          <Translate
            translationKey={TranslationKeys.REGISTRATION_STEP_PHONE_TITLE}
          />
        </WhmTypography>
        <WhmTypography variant="body2" color="GrayText">
          <Translate
            translationKey={TranslationKeys.REGISTRATION_STEP_PHONE_SUBTITLE}
          />
        </WhmTypography>
      </WhmStack>
      <WhmStack spacing={4} alignItems="center" sx={{ width: "100%" }}>
        <CountryTextField
          name="countryCode"
          value={formik.values.country?.value ?? ""}
          label={
            <Translate
              translationKey={TranslationKeys.REGISTRATION_RESIDENCE_COUNTRY}
            />
          }
          fieldError={formik.touched.country && Boolean(formik.errors.country)}
          helperText={
            formik.touched.country && formik.errors.country
              ? String(formik.errors.country)
              : undefined
          }
          onChange={(e, v) => {
            void formik.setFieldValue("country", v);
          }}
          onBlur={() => {
            void formik.setFieldTouched("country");
          }}
        />
        <WhmFade
          in={formik.values.country?.value === "US"}
          mountOnEnter
          unmountOnExit
          onEnter={resizeOnChange}
          onExited={() => {
            resizeOnChange && requestAnimationFrame(resizeOnChange);
          }}
        >
          <WhmAutocomplete
            options={statesUS}
            onBlur={() => void formik.setFieldTouched("state")}
            onChange={(e, v) => void formik.setFieldValue("state", v)}
            value={formik.values.state}
            fullWidth
            renderInput={(props) => (
              <WhmTextField
                {...props}
                label={
                  <Translate
                    translationKey={
                      TranslationKeys.REGISTRATION_RESIDENCE_STATE
                    }
                  />
                }
                name="state"
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={formik.touched.state && formik.errors.state}
              />
            )}
          />
        </WhmFade>
        <WhmTextField
          label={
            <Translate translationKey={TranslationKeys.REGISTRATION_PHONE} />
          }
          type="tel"
          fullWidth
          name="phone"
          value={formik.values.phone}
          inputProps={{ style: { width: "100%" } }}
          placeholder={
            formik.values.country
              ? intlTelInput.utils!.getExampleNumber(
                  formik.values.country.value,
                  true,
                  2,
                )
              : undefined
          }
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.phone && Boolean(formik.errors.phone)}
          helperText={formik.touched.phone && formik.errors.phone}
          InputProps={{
            startAdornment: (
              <WhmBox sx={{ marginRight: "0.5em" }}>
                +
                {formik.values.country
                  ? getDialCodeByCountryCode(formik.values.country.value)
                  : null}
              </WhmBox>
            ),
          }}
        />
      </WhmStack>
      {filler}
      <WhmButton type="submit" variant="contained" size="large" fullWidth>
        <Translate translationKey={TranslationKeys.REGISTRATION_NEXT} />
      </WhmButton>
    </WhmStack>
  );
}
